import Keycloak from 'keycloak-js'

/**
 * The portal is a public Keycloak client using the authorization code flow with
 * PKCE, so no client secret ever reaches the browser. The token is held in
 * memory only - never localStorage, where any script on the page could read it.
 */

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === 'true'

/** Refresh when the token has less than this left, so a request never carries an expired one. */
const MIN_TOKEN_VALIDITY_SECONDS = 30

let keycloak: Keycloak | null = null

export function isAuthDisabled(): boolean {
  return AUTH_DISABLED
}

export function getKeycloak(): Keycloak | null {
  return keycloak
}

export async function initAuth(): Promise<boolean> {
  if (AUTH_DISABLED) {
    // Local development against a service with security switched off.
    console.warn('[auth] Authentication is disabled. Never run this way in production.')
    return true
  }

  keycloak = new Keycloak({
    url: import.meta.env.VITE_KEYCLOAK_URL,
    realm: import.meta.env.VITE_KEYCLOAK_REALM,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  })

  const authenticated = await keycloak.init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    checkLoginIframe: false,
    // Round-trips through Keycloak land back where the user was.
    redirectUri: window.location.href,
  })

  if (authenticated) {
    scheduleRefresh()
  }
  return authenticated
}

/**
 * Returns a token that is valid for at least the next few seconds, refreshing
 * it first if needed. Every request goes through here.
 */
export async function getToken(): Promise<string | null> {
  if (AUTH_DISABLED || !keycloak) {
    return null
  }
  try {
    await keycloak.updateToken(MIN_TOKEN_VALIDITY_SECONDS)
    return keycloak.token ?? null
  } catch {
    // The refresh token has expired too; there is nothing to do but sign in again.
    await login()
    return null
  }
}

export async function login(): Promise<void> {
  if (!keycloak) {
    window.location.reload()
    return
  }
  await keycloak.login({ redirectUri: window.location.origin })
}

export async function logout(): Promise<void> {
  if (!keycloak) {
    window.location.href = '/'
    return
  }
  await keycloak.logout({ redirectUri: window.location.origin })
}

export function accountUrl(): string | null {
  return keycloak?.createAccountUrl() ?? null
}

/**
 * Keeps the token fresh in the background so a user who has been reading a
 * report for ten minutes does not hit a 401 on their next click.
 */
function scheduleRefresh(): void {
  window.setInterval(() => {
    keycloak?.updateToken(60).catch(() => {
      console.warn('[auth] Could not refresh the session; signing in again.')
      void login()
    })
  }, 30_000)
}
